// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { StyledButton } from "@insite/mobius/Clickable";
import DataTablePresentationPropsDefault from "@insite/mobius/DataTable/presentationProps";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { DataTableCssOverrides } from "@sc/MobiusOverrides/DataTable/DataTable";
import { xBaseTheme } from "@sc/XDL/types";
import { color, xScreen } from "@sc/XDL/Utilities";
import { css } from "styled-components";

const stickyMode = () => css`
    ${xScreen("lte", "sm")} {
        position: sticky;
        left: -5px;
        z-index: 2;
        background-clip: padding-box;
        background: inherit;
    }
`;

export default {
    defaultProps: {
        ...DataTablePresentationPropsDefault,
        mergeCss: true,
        cssOverrides: {
            header: css`
                background: ${color("common.ui2")};
                border: 1px solid ${color("common.border")};
                border-left: none;
                padding: 8px 12px;
                height: initial;
                line-height: 14px;
            `,
            head: css`
                background: ${color("common.ui2")};
                th:first-child {
                    border-top-left-radius: 4px;
                    border-bottom-left-radius: 4px;
                    border-left: 1px solid ${color("common.border")};
                }
                th:last-child {
                    border-top-right-radius: 4px;
                    border-bottom-right-radius: 4px;
                }
            `,
            cell: css`
                padding: 8px 12px;
                border-bottom: 0;
                height: initial;
                line-height: normal;
                &,
                ${StyledButton} > ${TypographyStyle} {
                    font-size: 14px;
                    line-height: 22px;
                }
            `,
            row: css`
                background: ${color("common.white")};
            `,
            evenRow: css`
                background: ${color("common.ui2")};
                td:first-child {
                    border-top-left-radius: 4px;
                    border-bottom-left-radius: 4px;
                }
                td:last-child {
                    border-top-right-radius: 4px;
                    border-bottom-right-radius: 4px;
                }
            `,
        } as DataTableCssOverrides,
        headerTypographyProps: {
            mergeCss: true,
            size: 12,
            weight: "bold",
            color: "common.black",
            css: css`
                letter-spacing: 0;
                line-height: 14px;
            `,
        },
        cellTypographyProps: {
            mergeCss: true,
            size: 14,
            weight: "normal",
            css: css`
                letter-spacing: 0;
                line-height: 22px;
            `,
        },
        sortClickableProps: {
            css: css`
                width: 100%;
                height: 100%;
            `,
        },
        sortIconProps: {
            size: 17,
            color: "common.black",
            css: css`
                margin-left: auto;

                svg {
                    padding-left: 5px;

                    &:hover {
                        opacity: 0.6;
                    }
                }
            `,
        },
        css: css`
            td[class^="DataTableCellBase"] {
                & > ${TypographyStyle}:empty:after {
                    content: "- -";
                }
            }
        `,
    },
} as xBaseTheme["dataTable"];
